import { useEffect, useRef, useState } from 'react';
import type Hls from 'hls.js';
import { ApiError, call } from '../api';
import { addonsApi, decodeCtx, findNextEpisode, getPlayQueue, progressApi, setPlayQueue, type StreamOption, type WatchCtx } from '../addons';
import { fmtClock, playAddonStream } from '../components/AddonSources';
import { prefsNow, usePrefs } from '../prefs';
import { subsApi, type SubResult } from '../api';
import { RatingCard } from '../components/RatingCard';
import { PlayerClock, PlayerInfoPanel, streamFlags } from '../components/PlayerOsd';
import { useMeta } from '../meta';
import { goBack, navigate } from '../router';
import { hevcSupported, watchForBlackVideo } from '../videoCheck';

type Mode = 'direct' | 'copy' | 'transcode';

/**
 * Speler voor een addon-bron. Eerst rechtstreeks (via het doorgeefluik van de server) als de browser het kan,
 * anders als HLS met het beeld ongewijzigd, en als dat ook niet lukt omgezet naar H.264.
 */
export function AddonPlayer({ params }: { params: URLSearchParams }) {
  const key = params.get('key') ?? '';
  const title = params.get('title') ?? 'Afspelen';
  const src = params.get('src') ?? '';
  const live = params.get('live') === '1';
  const [mode, setMode] = useState<Mode>(params.get('web') ? 'direct' : 'copy');
  const [error, setError] = useState<string | null>(null);
  const [serverTranscoded, setServerTranscoded] = useState(false);
  const [buffering, setBuffering] = useState(true);
  const [controls, setControls] = useState(true);
  const video = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const hideTimer = useRef<number | undefined>(undefined);

  // ---------- Reservebronnen: werkt deze bron niet, dan de volgende (max. 5 pogingen) ----------
  const tries = Number(params.get('fo')) || 1;
  const failover = (): boolean => {
    const pr = prefsNow();
    if (live || !params.get('q') || !pr.failover || tries >= (pr.failoverMax ?? 5)) return false;
    const q = getPlayQueue();
    if (!q || q.list[q.i]?.key !== key) return false;
    // Deze bron werkte niet: melden (twee keer = voortaan overslaan voor deze titel)
    const cur = q.list[q.i];
    if (cur?.fp && q.titleId) void addonsApi.failed(q.titleId, cur.fp, `${cur.addonName} · ${cur.name}`);
    const next = nextSourceIndex(q.list, q.i, pr);
    return next >= 0 && goTo(next, tries + 1);
  };
  /** Naar een andere bron uit de lijst, vanaf hetzelfde punt (bij wegvallen of zelf gekozen). */
  const goTo = (idx: number, fo: number): boolean => {
    const q = getPlayQueue();
    if (!q) return false;
    const s = q.list[idx];
    if (!s) return false;
    setPlayQueue({ ...q, i: idx });
    const q2 = new URLSearchParams({ key: s.key, title: q.title, src: `${s.addonName}${s.quality ? ` · ${s.quality}` : ''}`, q: '1', fo: String(fo) });
    // Al aan het kijken: verder op dezelfde plek
    const at = started.current ? Math.floor(pos().position) : 0;
    if (at > 5) q2.set('at', String(at));
    if (s.webReady) q2.set('web', '1');
    const c = params.get('ctx');
    if (c) {
      q2.set('ctx', c);
      q2.set('sa', s.addonName);
      q2.set('sn', s.name);
    }
    navigate(`/addon-play?${q2}`, { replace: true });
    return true;
  };
  const failoverRef = useRef(failover);
  failoverRef.current = failover;

  // ---------- Voortgang (Verder kijken, hervatten, Trakt/SIMKL) ----------
  const ctx = useRef(decodeCtx(params.get('ctx'))).current;
  const source = params.get('sa') && params.get('sn') ? { addon: params.get('sa')!, name: params.get('sn')! } : undefined;
  /** Waar we beginnen (hervatten); null = nog aan het opzoeken */
  const atParam = Number(params.get('at')) || 0;
  const [startAt, setStartAt] = useState<number | null>(atParam > 0 ? atParam : ctx && !live ? null : 0);
  const [resumed, setResumed] = useState<number | null>(null);
  /** Bij HLS via de server begint de stream op startSec en weet de server de totale duur */
  const timing = useRef<{ offset: number; duration: number }>({ offset: 0, duration: 0 });

  useEffect(() => {
    if (startAt !== null || !ctx) return;
    progressApi.resume(ctx.id, ctx.ratingKey)
      .then((r) => {
        const ok = r.position > 30 && (!r.duration || r.position < r.duration * 0.95);
        setStartAt(ok ? Math.max(0, r.position - 3) : 0);
        if (ok) setResumed(r.position);
      })
      .catch(() => setStartAt(0));
  }, []);

  const pos = () => {
    const el = video.current;
    if (!el) return { position: 0, duration: 0 };
    const duration = timing.current.duration > 0 ? timing.current.duration : Number.isFinite(el.duration) ? el.duration + timing.current.offset : 0;
    return { position: el.currentTime + timing.current.offset, duration };
  };
  const report = (state: 'playing' | 'paused' | 'stopped', keepalive = false) => {
    if (!ctx || live) return;
    const p = pos();
    if (!p.duration || p.position < 1) return;
    void progressApi.report({ ctx, position: p.position, duration: p.duration, state, source }, keepalive);
  };
  const reportRef = useRef(report);
  reportRef.current = report;

  useEffect(() => {
    if (!ctx || live) return;
    const el = video.current;
    const tick = window.setInterval(() => { if (el && !el.paused) reportRef.current('playing'); }, 15_000);
    const onPause = () => reportRef.current('paused');
    const onPlay = () => reportRef.current('playing');
    const onEnd = () => reportRef.current('stopped');
    const onHide = () => reportRef.current('stopped', true);
    el?.addEventListener('pause', onPause);
    el?.addEventListener('play', onPlay);
    el?.addEventListener('ended', onEnd);
    window.addEventListener('pagehide', onHide);
    return () => {
      window.clearInterval(tick);
      el?.removeEventListener('pause', onPause);
      el?.removeEventListener('play', onPlay);
      el?.removeEventListener('ended', onEnd);
      window.removeEventListener('pagehide', onHide);
      reportRef.current('stopped', true);
    };
  }, []);

  const restart = () => {
    setResumed(null);
    const el = video.current;
    if (!el) return;
    if (timing.current.offset > 0) {
      // Via de server: opnieuw starten vanaf het begin
      setStartAt(0);
      setRestartTick((n) => n + 1);
    } else el.currentTime = 0;
  };
  const [restartTick, setRestartTick] = useState(0);
  useEffect(() => {
    if (resumed === null) return;
    const t = window.setTimeout(() => setResumed(null), 9000);
    return () => window.clearTimeout(t);
  }, [resumed]);

  useEffect(() => {
    const el = video.current;
    if (!el || !key || startAt === null) return;
    let cancelled = false;
    let sid: string | null = null;
    setError(null);
    setBuffering(true);

    // Kan de browser het beeld niet aan: volgende stap (rechtstreeks → via server → omzetten)
    const next = (why: string) => {
      if (cancelled) return;
      if (mode === 'direct') setMode('copy');
      else if (mode === 'copy') setMode('transcode');
      else if (!failoverRef.current()) {
        setError(`Deze bron kan niet worden afgespeeld (${why}).`);
        setBuffering(false);
      }
    };
    const unwatch = watchForBlackVideo(el, () => next('geen beeld'));
    const fail = async (url: string | null) => {
      // De server weet vaak waarom (ffmpeg-melding)
      let msg = 'De bron is weggevallen.';
      if (url) {
        const r = await fetch(url, { credentials: 'same-origin' }).catch(() => null);
        const d = r && !r.ok ? ((await r.json().catch(() => null)) as { message?: string } | null) : null;
        if (d?.message) msg = d.message;
      }
      if (!cancelled && !failoverRef.current()) {
        setError(msg);
        setBuffering(false);
      }
    };

    const from = live ? 0 : startAt;
    addonsApi.play(key, mode === 'direct' ? { direct: true } : { transcode: mode === 'transcode', live, hevc: hevcSupported(), start: from || undefined })
      .then(async (r) => {
        sid = r.sessionId;
        timing.current = mode === 'direct' || !r.url ? { offset: 0, duration: 0 } : { offset: r.start ?? 0, duration: r.duration ?? 0 };
        // Rechtstreeks: zelf naar de juiste plek spoelen
        if ((mode === 'direct' || !r.url) && from > 0) el.addEventListener('loadedmetadata', () => { el.currentTime = from; }, { once: true });
        if (r.transcoded && mode === 'copy') setServerTranscoded(true);
        if (cancelled) {
          if (sid) void addonsApi.stop(sid);
          return;
        }
        el.onerror = () => {
          if (el.error) next(el.error.code === 4 ? 'formaat niet ondersteund' : el.error.code === 3 ? 'beeld kon niet worden gedecodeerd' : `fout ${el.error.code}`);
        };
        if (mode === 'direct' || !r.url) {
          el.src = r.direct;
          el.play().catch(() => undefined);
          return;
        }
        if (el.canPlayType('application/vnd.apple.mpegurl')) {
          el.src = r.url;
          el.play().catch(() => undefined);
          return;
        }
        const { default: HlsLib } = await import('hls.js');
        if (cancelled) return;
        // De server maakt de eerste stukjes nog: rustig opnieuw proberen
        const hls = new HlsLib({ manifestLoadingMaxRetry: 6, levelLoadingMaxRetry: 10, fragLoadingMaxRetry: 10, maxBufferLength: live ? 30 : 60, liveSyncDurationCount: 3 });
        hlsRef.current = hls;
        hls.on(HlsLib.Events.ERROR, (_e, d) => {
          if (!d.fatal) return;
          if (d.type === HlsLib.ErrorTypes.MEDIA_ERROR) next(d.details);
          else void fail(r.url);
        });
        hls.on(HlsLib.Events.MANIFEST_PARSED, () => {
          el.play().catch(() => undefined);
        });
        hls.loadSource(r.url);
        hls.attachMedia(el);
      })
      .catch((err) => {
        if (cancelled || failoverRef.current()) return;
        setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Deze bron kon niet gestart worden.');
        setBuffering(false);
      });

    const onHide = () => {
      if (sid) void addonsApi.stop(sid);
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      cancelled = true;
      unwatch();
      window.removeEventListener('pagehide', onHide);
      hlsRef.current?.destroy();
      hlsRef.current = null;
      el.onerror = null;
      el.removeAttribute('src');
      el.load();
      if (sid) void addonsApi.stop(sid);
    };
  }, [key, mode, startAt, restartTick]);

  // Start de bron niet binnen 35 seconden, dan de volgende proberen
  const started = useRef(false);
  // Beeld loopt echt (voor de leeftijdskaart)
  const [running, setRunning] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (!started.current) failoverRef.current();
    }, 35_000);
    return () => window.clearTimeout(t);
  }, [key]);

  // ---------- Volgende aflevering: tegen het einde alvast zoeken, na afloop starten ----------
  const nextEp = useRef<{ ctx: WatchCtx; streams: StreamOption[] } | null | undefined>(undefined);
  const [upNext, setUpNext] = useState<{ ctx: WatchCtx; streams: StreamOption[]; left: number } | null>(null);
  useEffect(() => {
    if (live || !ctx || ctx.type !== 'series') return;
    const el = video.current;
    if (!el) return;
    const onTime = () => {
      if (nextEp.current !== undefined || !prefsNow().prefetchNext) return;
      const p = pos();
      if (p.duration > 0 && (p.duration - p.position < 240 || p.position / p.duration > 0.9)) {
        nextEp.current = null;
        void findNextEpisode(ctx).then((n) => { nextEp.current = n; });
      }
    };
    const onEnded = () => {
      const pr = prefsNow();
      if (!pr.autoplayNext) return;
      const go = (n: { ctx: WatchCtx; streams: StreamOption[] } | null) => {
        if (n) setUpNext({ ...n, left: pr.nextCountdown });
      };
      if (nextEp.current) go(nextEp.current);
      else if (nextEp.current === undefined || nextEp.current === null) void findNextEpisode(ctx).then(go);
    };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnded);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('ended', onEnded);
    };
  }, []);
  const playNext = (n: { ctx: WatchCtx; streams: StreamOption[] }) => {
    const best = n.streams[0];
    if (!best) return;
    const t = `${n.ctx.name} · S${n.ctx.season} E${n.ctx.episode}`;
    playAddonStream(best, t, false, n.ctx, n.streams, { replace: true });
  };
  useEffect(() => {
    if (!upNext) return;
    if (upNext.left <= 0) {
      playNext(upNext);
      return;
    }
    const t = window.setTimeout(() => setUpNext((u) => (u ? { ...u, left: u.left - 1 } : u)), 1000);
    return () => window.clearTimeout(t);
  }, [upNext]);

  // ---------- Ondertitels (OpenSubtitles) ----------
  const prefs = usePrefs();
  const [subs, setSubs] = useState<SubResult[] | null>(null);
  const [subId, setSubId] = useState<number | null>(null);
  const [subDelay, setSubDelay] = useState(0);
  const [subMenu, setSubMenu] = useState(false);
  const [subMsg, setSubMsg] = useState<string | null>(null);
  const applied = useRef(0);
  useEffect(() => {
    if (!ctx || live) return;
    const q = params.get('q') ? getPlayQueue() : null;
    const cur = q?.list.find((x) => x.key === key);
    const release = cur ? `${cur.title} ${cur.name}` : params.get('sn') ?? '';
    const langs = prefsNow().subLangs.length ? prefsNow().subLangs : ['nld'];
    subsApi.search({ id: ctx.id, tmdb: ctx.tmdbId, type: ctx.type, release, langs })
      .then((r) => {
        if (!r.configured) return;
        setSubs(r.results);
        // Automatisch aan (Instellingen → Audio en ondertitels), behalve als de bron al in jouw taal gesproken is
        const mode = prefsNow().subMode;
        const nlAudio = cur?.traits?.nl === 'audio' && langs[0] === 'nld';
        if ((mode === 'auto' || mode === 'always') && !nlAudio && r.results[0]) setSubId(r.results[0].fileId);
      })
      .catch(() => setSubs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  // Vertraging, automatische synchronisatie (snelheid + verschuiving) en het beginpunt van de server
  // op de cues toepassen, steeds vanaf de originele tijden
  const orig = useRef<WeakMap<TextTrackCue, { s: number; e: number }>>(new WeakMap());
  const [fix, setFix] = useState<{ offset: number; scale: number } | null>(null);
  const shiftCues = (track: TextTrack | null | undefined) => {
    if (!track?.cues) return;
    const sc = fix?.scale ?? 1;
    const off = (fix?.offset ?? 0) + subDelay - timing.current.offset;
    for (let i = 0; i < track.cues.length; i++) {
      const c = track.cues[i];
      let o = orig.current.get(c);
      if (!o) { o = { s: c.startTime, e: c.endTime }; orig.current.set(c, o); }
      c.startTime = Math.max(0, o.s * sc + off);
      c.endTime = Math.max(0, o.e * sc + off);
    }
    applied.current = off;
  };
  useEffect(() => {
    shiftCues(video.current?.textTracks?.[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subDelay, fix]);
  // Automatisch synchroniseren: gelijkzetten met een ondertitel die bij deze bron past (andere taal mag)
  const [syncing, setSyncing] = useState(false);
  const syncRef = subs?.find((r) => r.fileId !== subId && r.match >= 60) ?? null;
  const autoSync = async () => {
    if (!subId || !syncRef) return;
    setSyncing(true);
    setSubMsg(null);
    try {
      const r = await call<{ offset: number; scale: number; score: number; ok: boolean }>(`/api/subs/sync/${subId}?ref=${syncRef.fileId}`);
      if (!r.ok) { setSubMsg('Kon geen goede timing vinden; stel hem met de knoppen bij.'); return; }
      setFix({ offset: r.offset, scale: r.scale });
      try { localStorage.setItem(`subsync:${subId}`, JSON.stringify({ offset: r.offset, scale: r.scale })); } catch { /* geen opslag */ }
      setSubMsg(`Gelijkgezet${r.scale !== 1 ? ' (andere beeldsnelheid)' : ''}: ${r.offset > 0 ? '+' : ''}${r.offset.toFixed(1).replace('.', ',')} s.`);
    } catch {
      setSubMsg('Synchroniseren mislukt.');
    } finally {
      setSyncing(false);
    }
  };
  useEffect(() => {
    applied.current = 0;
    // Gekozen vertraging en synchronisatie per ondertitel onthouden
    try { setSubDelay(Number(localStorage.getItem(`subdelay:${subId}`)) || 0); } catch { setSubDelay(0); }
    try { const f = localStorage.getItem(`subsync:${subId}`); setFix(f ? (JSON.parse(f) as { offset: number; scale: number }) : null); } catch { setFix(null); }
  }, [subId]);
  // Past de gekozen ondertitel niet bij deze bron, maar een andere wel? Dan meteen automatisch gelijkzetten
  useEffect(() => {
    if (!subId || !subs || !syncRef) return;
    const cur = subs.find((r) => r.fileId === subId);
    let stored = false;
    try { stored = !!localStorage.getItem(`subsync:${subId}`); } catch { /* */ }
    if (cur && cur.match < 60 && !stored) void autoSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subId, subs]);
  const changeDelay = (d: number) => {
    const v = Math.round((subDelay + d) * 10) / 10;
    setSubDelay(v);
    try { localStorage.setItem(`subdelay:${subId}`, String(v)); } catch { /* geen opslag */ }
  };

  // ---------- Eigen bediening (zelfde als de Plex-speler) ----------
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [menu, setMenu] = useState<null | 'sources'>(null);
  useEffect(() => {
    const el = video.current;
    if (!el) return;
    const upd = () => { const p = pos(); setTime(p.position); setDur(p.duration); };
    const on = () => setPlaying(true);
    const off = () => setPlaying(false);
    el.addEventListener('timeupdate', upd);
    el.addEventListener('durationchange', upd);
    el.addEventListener('play', on);
    el.addEventListener('pause', off);
    return () => {
      el.removeEventListener('timeupdate', upd);
      el.removeEventListener('durationchange', upd);
      el.removeEventListener('play', on);
      el.removeEventListener('pause', off);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const togglePlay = () => {
    const el = video.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => undefined);
    else el.pause();
    poke();
  };
  /** Spoelen: binnen wat de server al klaar heeft gewoon springen, anders de server vanaf dat punt laten beginnen. */
  const seekTo = (abs: number) => {
    const el = video.current;
    if (!el) return;
    const t = Math.max(0, dur > 0 ? Math.min(abs, dur - 2) : abs);
    const off = timing.current.offset;
    const viaServer = timing.current.duration > 0 || off > 0;
    if (!viaServer) { el.currentTime = t; return; }
    const rel = t - off;
    const end = el.seekable.length ? el.seekable.end(el.seekable.length - 1) : 0;
    if (rel >= 0 && rel <= end - 2) { el.currentTime = rel; return; }
    setResumed(null);
    setStartAt(t);
    setRestartTick((n) => n + 1);
  };
  const seekBy = (d: number) => { seekTo(pos().position + d); poke(); };
  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void document.documentElement.requestFullscreen().catch(() => undefined);
  };
  // Valt de bron midden in de film weg (blijft hangen, standaard 25 s), dan de volgende bron vanaf hetzelfde punt
  useEffect(() => {
    if (!buffering || !started.current || error) return;
    const t = window.setTimeout(() => { if (started.current) failoverRef.current(); }, (prefsNow().failoverStall ?? 25) * 1000);
    return () => window.clearTimeout(t);
  }, [buffering, error]);
  // Info in de speler: logo, scores en labels van de bron
  const osdMeta = useMeta(ctx?.tmdbId ? { type: ctx.type === 'series' ? 'tv' : 'movie', tmdbId: ctx.tmdbId } : null);
  const queue = params.get('q') ? getPlayQueue() : null;
  const curStream = queue?.list.find((x) => x.key === key) ?? null;

  const poke = () => {
    setControls(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setControls(false), 3500);
  };

  useEffect(() => {
    poke();
    return () => window.clearTimeout(hideTimer.current);
  }, []);

  return (
    <div
      className={`player ${controls ? 'show-controls' : ''}`}
      onMouseMove={poke}
      onClick={poke}
      data-sub-size={prefs.subStyle.size}
      data-sub-color={prefs.subStyle.color}
      data-sub-bg={prefs.subStyle.background}
    >
      {ctx && !live && (
        <RatingCard
          show={running}
          imdb={/^tt\d+/.test(ctx.id) ? ctx.id.split(':')[0] : null}
          tmdb={ctx.tmdbId ?? null}
          type={ctx.type === 'series' ? 'tv' : 'movie'}
          tech={params.get('src')?.split(' · ').slice(1).join(' · ') || null}
        />
      )}
      <video
        ref={video}
        className="player-video"
        playsInline
        onClick={(e) => { e.stopPropagation(); if (controls) togglePlay(); poke(); }}
        onWaiting={() => setBuffering(true)}
        onPlaying={() => { started.current = true; setRunning(true); setBuffering(false); shiftCues(video.current?.textTracks?.[0]); }}
        onCanPlay={() => setBuffering(false)}
      >
        {subId && (
          <track
            key={`${subId}:${restartTick}:${mode}`}
            kind="subtitles"
            src={subsApi.fileUrl(subId)}
            default
            label="Ondertitels"
            onLoad={(e) => {
              const t = e.currentTarget.track;
              t.mode = 'showing';
              applied.current = 0;
              orig.current = new WeakMap();
              shiftCues(t);
              if (prefs.subStyle.position === 'raised' && t.cues) for (let i = 0; i < t.cues.length; i++) { const c = t.cues[i] as VTTCue; c.snapToLines = true; c.line = -4; }
            }}
            onError={() => {
              void fetch(subsApi.fileUrl(subId), { credentials: 'same-origin' }).then(async (r) => {
                const d = (await r.json().catch(() => null)) as { message?: string } | null;
                setSubMsg(d?.message ?? 'Ondertitels laden mislukt.');
              });
            }}
          />
        )}
      </video>
      {buffering && !error && <div className="player-spinner" role="status" aria-label="Laden" />}
      {resumed !== null && !error && (
        <div className="player-resume" role="status">
          <span>Verder vanaf {fmtClock(resumed)}</span>
          <button className="btn btn-sm" onClick={restart}>Opnieuw beginnen</button>
        </div>
      )}
      {tries > 1 && !error && buffering && (
        <div className="player-resume" role="status"><span>Vorige bron werkte niet; bron {tries} wordt geprobeerd…</span></div>
      )}
      {upNext && (
        <div className="player-resume player-upnext" role="status">
          <span>Volgende aflevering: S{upNext.ctx.season} E{upNext.ctx.episode}{upNext.left > 0 ? ` over ${upNext.left} s` : ''}</span>
          <button className="btn btn-primary btn-sm" data-autofocus onClick={() => playNext(upNext)}>Nu kijken</button>
          <button className="btn btn-sm" onClick={() => setUpNext(null)}>Annuleren</button>
        </div>
      )}
      {error && (
        <div className="player-error" role="alert">
          <p>{error}</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" data-autofocus onClick={() => { setError(null); setMode('transcode'); }}>Opnieuw (omzetten)</button>
            <button className="btn" onClick={goBack}>Terug</button>
          </div>
        </div>
      )}
      <div className="player-top" aria-hidden={!controls}>
        <button className="icon-btn" onClick={goBack} aria-label="Terug">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <div className="player-title">
          <strong>{title}</strong>
          <span className="muted">{ctx?.season != null && ctx.episode != null ? `S${ctx.season} · A${ctx.episode}${ctx.epTitle ? ` · ${ctx.epTitle}` : ''}` : src}</span>
        </div>
        <span className={`mode-badge ${mode === 'transcode' || serverTranscoded ? 'is-transcode' : ''}`}>
          {mode === 'direct' ? 'Direct' : mode === 'transcode' || serverTranscoded ? 'Omgezet door de server' : 'Via de server'}{src ? ` · ${src}` : ''}
        </span>
        {!live && <PlayerClock remaining={dur > 0 ? dur - time : null} />}
      </div>

      <div className="player-bottom" aria-hidden={!controls} onClick={(e) => e.stopPropagation()}>
        {ctx && !live && (
          <PlayerInfoPanel
            info={{ title, line: ctx.season != null && ctx.episode != null ? `S${ctx.season} · A${ctx.episode}${ctx.epTitle ? ` · ${ctx.epTitle}` : ''}` : null, flags: streamFlags(curStream) }}
            meta={osdMeta}
          />
        )}
        {!live ? (
          <div className="scrub">
            <span className="time">{fmtClock(time)}</span>
            <input
              type="range"
              className="scrub-range"
              aria-label="Positie"
              min={0}
              max={Math.max(1, Math.floor(dur))}
              step={1}
              value={Math.floor(time)}
              onChange={(e) => { setTime(Number(e.target.value)); poke(); }}
              onPointerUp={(e) => seekTo(Number((e.target as HTMLInputElement).value))}
              onKeyUp={(e) => seekTo(Number((e.target as HTMLInputElement).value))}
            />
            <span className="time">-{fmtClock(Math.max(0, dur - time))}</span>
          </div>
        ) : (
          <div className="scrub"><span className="live-pill">LIVE</span></div>
        )}
        <div className="player-buttons" data-focus-group>
          {!live && (
            <button className="icon-btn" onClick={() => seekBy(-10)} aria-label="10 seconden terug">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>
            </button>
          )}
          <button className="icon-btn icon-btn-lg" onClick={togglePlay} aria-label={playing ? 'Pauzeren' : 'Afspelen'} data-autofocus>
            {playing ? (
              <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
            ) : (
              <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z" /></svg>
            )}
          </button>
          {!live && (
            <button className="icon-btn" onClick={() => seekBy(30)} aria-label="30 seconden vooruit">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 4v5h-5" /></svg>
            </button>
          )}
          <div className="spacer" />
          {subs !== null && (
            <button className="chip chip-lang" aria-expanded={subMenu} onClick={() => { setMenu(null); setSubMenu((m) => !m); }}>
              Ondertitels<small>{subId ? (subs.find((x) => x.fileId === subId)?.lang ?? '').slice(0, 3).toUpperCase() || 'AAN' : 'UIT'}</small>
            </button>
          )}
          {queue && queue.list.length > 1 && !live && (
            <button className="chip" aria-expanded={menu === 'sources'} onClick={() => { setSubMenu(false); setMenu(menu === 'sources' ? null : 'sources'); }}>Bronnen</button>
          )}
          <button className="icon-btn" onClick={toggleFullscreen} aria-label="Volledig scherm">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
          </button>
        </div>
        {menu === 'sources' && queue && (
          <div className="player-menu" role="menu" data-focus-group>
            {queue.list.slice(0, 15).map((x, i) => (
              <button key={x.key} role="menuitemradio" aria-checked={x.key === key} className="menu-item" onClick={() => { if (x.key !== key) goTo(i, tries); }}>
                {[x.quality, x.addonName].filter(Boolean).join(' · ')} <span className="subtle">· {x.name.replace(/\s+/g, ' ').slice(0, 60)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {subMenu && subs !== null && (
        <div className="sub-menu" role="dialog" aria-label="Ondertitels" onClick={(e) => e.stopPropagation()}>
          <div className="sub-menu-head">
            <strong>Ondertitels</strong>
            <span className="subtle">OpenSubtitles en je addons</span>
          </div>
          {subId && (
            <div className="sub-delay" role="group" aria-label="Timing">
              <span className="subtle">Timing</span>
              <button className="btn btn-sm" onClick={() => changeDelay(-0.5)} aria-label="Ondertitels eerder">−0,5 s</button>
              <strong>{subDelay > 0 ? '+' : ''}{subDelay.toFixed(1).replace('.', ',')} s</strong>
              <button className="btn btn-sm" onClick={() => changeDelay(0.5)} aria-label="Ondertitels later">+0,5 s</button>
            </div>
          )}
          {subId && syncRef && (
            <div className="sub-delay">
              <button className="btn btn-sm" disabled={syncing} onClick={() => void autoSync()}>{syncing ? 'Bezig…' : fix ? 'Opnieuw synchroniseren' : 'Automatisch synchroniseren'}</button>
              {fix && <button className="btn btn-ghost btn-sm" onClick={() => { setFix(null); try { localStorage.removeItem(`subsync:${subId}`); } catch { /* */ } }}>Origineel</button>}
            </div>
          )}
          {subMsg && <p className="subtle" role="status">{subMsg}</p>}
          <ul className="sub-list">
            <li><button className={!subId ? 'is-on' : ''} onClick={() => setSubId(null)}>Uit</button></li>
            {subs.map((r) => (
              <li key={r.fileId}>
                <button className={subId === r.fileId ? 'is-on' : ''} onClick={() => { setSubMsg(null); setSubId(r.fileId); }}>
                  <span className="sub-lang">{r.lang.toUpperCase()}</span>
                  <span className="sub-rel">{r.release}</span>
                  <span className="sub-meta">
                    {r.match >= 60 && <span className="sub-match">past bij deze bron</span>}
                    {r.hearingImpaired && <span>SDH</span>}
                    {r.source && r.source !== 'OpenSubtitles' ? <span>{r.source}</span> : <span>{r.downloads.toLocaleString('nl-NL')}×</span>}
                  </span>
                </button>
              </li>
            ))}
            {subs.length === 0 && <li className="subtle">Niets gevonden in je talen.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * De volgende bron volgens je regels: geen geblokkeerde, alleen bronnen die klaarstaan bij debrid (als dat aanstaat)
 * en eerst dezelfde kwaliteit (als dat aanstaat; is die er niet meer, dan de beste andere).
 */
function nextSourceIndex(list: { quality: string | null; blocked?: { auto: boolean } | null; traits?: { cached: boolean | null } }[], from: number, pr: { failoverSameQuality?: boolean; failoverCachedOnly?: boolean }): number {
  const cur = list[from];
  const ok = (i: number) => !list[i].blocked && !(pr.failoverCachedOnly !== false && list[i].traits?.cached === false);
  const after = list.map((_, i) => i).filter((i) => i > from && ok(i));
  if (pr.failoverSameQuality && cur?.quality) {
    const same = after.find((i) => list[i].quality === cur.quality);
    if (same !== undefined) return same;
  }
  return after[0] ?? -1;
}
